import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import { format } from 'date-fns';
import { WASTE_CATEGORIES, getCategoryColor, getCategoryIcon } from '../config/wasteCategories';
import { toast } from 'react-toastify';
import { FaMapMarkerAlt, FaClock, FaUser, FaComment, FaCheckCircle, FaCamera } from 'react-icons/fa';
import L from 'leaflet';
import './WasteDetail.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const WasteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  
  const [waste, setWaste] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [cleanupEvidence, setCleanupEvidence] = useState('');
  const [cleanupPhoto, setCleanupPhoto] = useState(null);
  const [cleanupPhotoPreview, setCleanupPhotoPreview] = useState(null);
  const [submittingCleanup, setSubmittingCleanup] = useState(false);

  useEffect(() => {
    const fetchWasteDetail = async () => {
      try {
        const data = await api.getWasteReport(id);
        setWaste(data);
      } catch (error) {
        console.error('Error fetching waste detail:', error);
        toast.error('Waste report not found');
        navigate('/map');
      } finally {
        setLoading(false);
      }
    };
    fetchWasteDetail();
  }, [id, navigate]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    
    if (!comment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    if (!currentUser) {
      toast.error('Please log in to comment');
      return;
    }

    try {
      setSubmittingComment(true);
      
      await api.addComment(id, {
        text: comment,
        author: currentUser.displayName || currentUser.email,
        authorId: currentUser.uid
      });

      setComment('');
      toast.success('Comment added successfully');
      
      const updatedWaste = await api.getWasteReport(id);
      setWaste(updatedWaste);
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleCleanupPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCleanupPhoto(file);
      setCleanupPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleCleanupSubmit = async (e) => {
    e.preventDefault();

    if (!cleanupEvidence.trim() && !cleanupPhoto) {
      toast.error('Please provide cleanup evidence (text or photo)');
      return;
    }

    if (userProfile?.role !== 'subscriber') {
      toast.error('Only subscribers can submit cleanup evidence');
      return;
    }

    try {
      setSubmittingCleanup(true);

      const reportTime = new Date(waste.timestamp);
      const cleanupTime = new Date();
      const durationMs = cleanupTime - reportTime;
      const durationHours = Math.floor(durationMs / (1000 * 60 * 60));
      const durationDays = Math.floor(durationHours / 24);
      const remainingHours = durationHours % 24;

      const cleanupData = {
        evidence: cleanupEvidence,
        submittedBy: currentUser.displayName || currentUser.email,
        submittedById: currentUser.uid,
        duration: {
          days: durationDays,
          hours: remainingHours,
          totalHours: durationHours
        }
      };

      await api.submitCleanup(id, cleanupData, cleanupPhoto);

      toast.success('Cleanup evidence submitted successfully');
      setCleanupEvidence('');
      setCleanupPhoto(null);
      setCleanupPhotoPreview(null);
      
      const updatedWaste = await api.getWasteReport(id);
      setWaste(updatedWaste);
    } catch (error) {
      console.error('Error submitting cleanup:', error);
      toast.error('Failed to submit cleanup evidence');
    } finally {
      setSubmittingCleanup(false);
    }
  };

  if (loading) {
    return (
      <div className="waste-detail-page">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!waste) {
    return null;
  }

  const category = WASTE_CATEGORIES[waste.category];

  return (
    <div className="waste-detail-page">
      <div className="waste-detail-container">
        <button className="btn btn-outline" onClick={() => navigate('/map')}>
          Back to Map
        </button>

        <div className="waste-detail-card">
          <div className="waste-header">
            <div className="category-badge" style={{ backgroundColor: getCategoryColor(waste.category) }}>
              {getCategoryIcon(waste.category)} {category?.name}
            </div>
            <div className={`status-badge ${waste.status}`}>
              {waste.status === 'cleaned' ? <FaCheckCircle /> : null} {waste.status}
            </div>
          </div>

          <h1 className="waste-title">{waste.wasteType}</h1>

          <div className="info-grid">
            <div className="info-item">
              <FaUser />
              <div>
                <strong>Reported by</strong>
                <p>{waste.reportedBy}</p>
              </div>
            </div>
            <div className="info-item">
              <FaClock />
              <div>
                <strong>Date & Time</strong>
                <p>{format(new Date(waste.timestamp), 'MMM dd, yyyy HH:mm')}</p>
              </div>
            </div>
            <div className="info-item">
              <FaMapMarkerAlt />
              <div>
                <strong>Location</strong>
                <p>{waste.location.lat.toFixed(6)}, {waste.location.lng.toFixed(6)}</p>
              </div>
            </div>
          </div>

          {waste.description && (
            <div className="description-section">
              <h3>Description</h3>
              <p>{waste.description}</p>
            </div>
          )}

          <div className="evidence-section">
            <h3>Evidence ({waste.evidenceType})</h3>
            {waste.photoURL && (
              <div className="evidence-media">
                <img src={waste.photoURL} alt="Waste evidence" />
              </div>
            )}
            {waste.videoURL && (
              <div className="evidence-media">
                <video src={waste.videoURL} controls />
              </div>
            )}
            {waste.audioURL && (
              <div className="evidence-media">
                <audio src={waste.audioURL} controls />
              </div>
            )}
            {waste.evidenceType === 'text' && !waste.photoURL && !waste.videoURL && !waste.audioURL && (
              <p>Text evidence only</p>
            )}
          </div>

          <div className="map-section">
            <h3>Location on Map</h3>
            <div className="map-container">
              <MapContainer
                center={[waste.location.lat, waste.location.lng]}
                zoom={15}
                style={{ height: '300px', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={[waste.location.lat, waste.location.lng]} />
              </MapContainer>
            </div>
          </div>

          {waste.cleanupEvidence && (
            <div className="cleanup-section">
              <h3><FaCheckCircle /> Cleanup Completed</h3>
              <div className="cleanup-info">
                <p><strong>Cleaned by:</strong> {waste.cleanupEvidence.submittedBy}</p>
                <p><strong>Date:</strong> {format(new Date(waste.cleanupEvidence.timestamp), 'MMM dd, yyyy HH:mm')}</p>
                <p><strong>Duration:</strong> {waste.cleanupEvidence.duration.days} days, {waste.cleanupEvidence.duration.hours} hours</p>
                {waste.cleanupEvidence.evidence && (
                  <p><strong>Notes:</strong> {waste.cleanupEvidence.evidence}</p>
                )}
                {waste.cleanupEvidence.photoURL && (
                  <div className="cleanup-photo">
                    <img src={waste.cleanupEvidence.photoURL} alt="Cleanup evidence" />
                  </div>
                )}
              </div>
            </div>
          )}

          {userProfile?.role === 'subscriber' && waste.status !== 'cleaned' && (
            <div className="cleanup-form-section">
              <h3>Submit Cleanup Evidence</h3>
              <form onSubmit={handleCleanupSubmit} className="cleanup-form">
                <div className="form-group">
                  <label htmlFor="cleanupEvidence">Cleanup Notes</label>
                  <textarea
                    id="cleanupEvidence"
                    value={cleanupEvidence}
                    onChange={(e) => setCleanupEvidence(e.target.value)}
                    rows="3"
                    placeholder="Describe the cleanup action taken..."
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="cleanupPhoto">
                    <FaCamera /> Upload Cleanup Photo (Optional)
                  </label>
                  <input
                    type="file"
                    id="cleanupPhoto"
                    accept="image/*"
                    onChange={handleCleanupPhotoChange}
                  />
                  {cleanupPhotoPreview && (
                    <div className="preview">
                      <img src={cleanupPhotoPreview} alt="Cleanup preview" />
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submittingCleanup}
                >
                  {submittingCleanup ? 'Submitting...' : 'Submit Cleanup Evidence'}
                </button>
              </form>
            </div>
          )}

          <div className="comments-section">
            <h3><FaComment /> Comments ({waste.comments?.length || 0})</h3>
            
            {currentUser && (
              <form onSubmit={handleCommentSubmit} className="comment-form">
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a comment..."
                  rows="3"
                />
                <button
                  type="submit"
                  className="btn btn-secondary"
                  disabled={submittingComment}
                >
                  {submittingComment ? 'Posting...' : 'Post Comment'}
                </button>
              </form>
            )}

            <div className="comments-list">
              {waste.comments && waste.comments.length > 0 ? (
                waste.comments.map((c, index) => (
                  <div key={index} className="comment-item">
                    <div className="comment-header">
                      <strong>{c.author}</strong>
                      <span>{format(new Date(c.timestamp), 'MMM dd, yyyy HH:mm')}</span>
                    </div>
                    <p>{c.text}</p>
                  </div>
                ))
              ) : (
                <p className="no-comments">No comments yet. Be the first to comment!</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WasteDetail;
